import { Box, FormGroup, Grid, Modal, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { fontSize } from "../theme/fontSize";
import { fontWeight } from "../theme/fontWeight";
import { FontFamily } from "../theme/fontFamily";
import { ThemeColor } from "../theme/color";
import { Formik, Form } from "formik";
import CustomButton from "../button/button";
import moment from "moment";
import * as Yup from "yup";
import {
  GetStudentReminderByIdAction,
} from "../../Redux/action/ApplicationAction";
import { AddReminderService } from "../../Redux/service/Application";
import { useState } from "react";
import { useDispatch } from "react-redux";
import MyDatePicker from "../datepicker/OutlinedDatepicker";
import dayjs from "dayjs";
import OutlinedTextAreaField from "../inputfiled/OutlinedTextedArea";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import { StudentAllReminderModal } from "./AllReminderModal.component";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "85%", md: "600px" },
  maxHeight: "85vh",
  bgcolor: "#FFFFFF",
  borderRadius: "16px",
  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
  border: "none",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const validationReminderSchema = Yup.object({
  text: Yup.string().required("Reminder Text is required."),
  reminderDateTime: Yup.date()
    .required("Reminder date is required")
    .test(
      "is-greater",
      "Reminder date and time must be after current time.",
      function (value) {
        return dayjs(value).isAfter(dayjs(), "millisecond");
      }
    ),
  resendDateTime: Yup.date()
    .required("Resend date is required")
    .test(
      "is-greater",
      "Resend date must be after reminder date",
      function (value) {
        const { reminderDateTime } = this.parent;
        if (!reminderDateTime || !value) return true;
        return dayjs(value).isAfter(dayjs(reminderDateTime));
      }
    ),
});

export const StudentReminderModal = ({
  ReminderModal,
  handleClose,
  stdId,
  Studentlist,
}) => {
  const dispatch = useDispatch();
  const [AllReminderModal, setAllReminderModal] = useState(false);

  const handleCloseAllReminderModal = () => setAllReminderModal(!AllReminderModal);

  const currentStudent = Studentlist?.find(
    (data) => data.id === stdId || data._id === stdId
  );
  const studentName =
    currentStudent?.studentFirstName ||
    currentStudent?.firstName ||
    "";

  return (
    <>
      <Modal
        open={ReminderModal}
        onClose={() => handleClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              bgcolor: ThemeColor.PURPLE,
              color: "#FFFFFF",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <NotificationsActiveIcon sx={{ color: "#FFF", fontSize: 22 }} />
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={fontWeight.W600}
                fontSize={fontSize.h3.medium}
                color="#FFFFFF"
              >
                {studentName ? `New Reminder for "${studentName}"` : "New Reminder"}
              </Typography>
            </Box>
            <IconButton
              onClick={() => handleClose()}
              size="small"
              sx={{
                color: "#FFFFFF",
                bgcolor: "rgba(255, 255, 255, 0.15)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Form Body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              backgroundColor: "#FFFFFF",
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: '#F1F5F9' },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '10px' },
            }}
          >
            <Formik
              initialValues={{ text: "", reminderDateTime: null, resendDateTime: null }}
              validationSchema={validationReminderSchema}
              onSubmit={async (values, { resetForm }) => {
                values["student"] = stdId;
                await AddReminderService(values)
                  .then((res) => {
                    if (res.status === 200) {
                      handleClose();
                      setAllReminderModal(true);
                      dispatch(GetStudentReminderByIdAction(stdId));
                      dispatch(showSnackbar(res.data.message, "success"));
                      resetForm();
                    }
                  })
                  .catch((e) => {
                    dispatch(showSnackbar(e.response?.data?.message || "Error setting reminder", "error"));
                  });
              }}
            >
              {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
                <Form onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography
                        fontFamily={FontFamily.Poppins}
                        fontWeight={fontWeight.W500}
                        fontSize="0.875rem"
                        color="#475569"
                        mb={0.5}
                      >
                        Reminder Message *
                      </Typography>
                      <OutlinedTextAreaField
                        multiline={true}
                        minRows={3}
                        value={values.text}
                        onChange={handleChange}
                        name={"text"}
                        placeholder="Type reminder details..."
                        fullWidth={true}
                        helperText={errors.text}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        fontFamily={FontFamily.Poppins}
                        fontWeight={fontWeight.W500}
                        fontSize="0.875rem"
                        color="#475569"
                        mb={0.5}
                      >
                        Reminder Date & Time *
                      </Typography>
                      <MyDatePicker
                        minDate={dayjs().startOf("day")}
                        label="Select Date & Time"
                        onChange={(date) => {
                          setFieldValue(
                            `reminderDateTime`,
                            dayjs(date).format("YYYY-MM-DD HH:mm")
                          );
                        }}
                        helperText={errors.reminderDateTime}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        fontFamily={FontFamily.Poppins}
                        fontWeight={fontWeight.W500}
                        fontSize="0.875rem"
                        color="#475569"
                        mb={0.5}
                      >
                        Resend Date & Time *
                      </Typography>
                      <MyDatePicker
                        minDate={dayjs().startOf("day")}
                        label="Select Resend Date & Time"
                        onChange={(date) => {
                          setFieldValue(
                            `resendDateTime`,
                            dayjs(date).format("YYYY-MM-DD HH:mm")
                          );
                        }}
                        helperText={errors.resendDateTime}
                      />
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Typography
                        color={ThemeColor.BLUE}
                        fontSize="0.9rem"
                        fontWeight={fontWeight.W600}
                        fontFamily={FontFamily.Poppins}
                        sx={{ cursor: "pointer", textDecoration: "underline", "&:hover": { color: ThemeColor.PURPLE } }}
                        onClick={() => {
                          setAllReminderModal(true);
                          handleClose();
                        }}
                      >
                        Show All Reminders
                      </Typography>
                      <CustomButton
                        variant="contained"
                        type="submit"
                        backgroundColor={ThemeColor.PURPLE}
                        color={ThemeColor.WHITE}
                      >
                        Set Reminder
                      </CustomButton>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Modal>

      <StudentAllReminderModal
        AllReminderModal={AllReminderModal}
        handleClose={handleCloseAllReminderModal}
        dispatch={dispatch}
        Studentlist={Studentlist}
        stdId={stdId}
        handleCloseReminderModal={handleClose}
      />
    </>
  );
};
